'use client'

import {useEffect, useMemo, useState} from 'react'
import {Edit, Plus, RefreshCw, Tag, Trash2, User, X} from 'lucide-react'
import type {Account, Contact, ContactLabel} from '@/types'
import {PageHeader} from '@/components/PageHeader'
import {message} from '@/components/Toast'
import {ContactProfilePopover} from '@/components/ContactProfilePopover'
import {useLabels} from '@/hooks/useLabels'
import {avatarDisplaySrc, contactLatestAvatarUrl, displayName} from '@/lib/utils'

/**
 * Label manager. Split-pane layout ported from WechatBotConsole.jsx:
 *
 *   ┌────────────┬──────────────────────────────────┐
 *   │ 所有标签   │ <selectedTag.name>  (n 人)       │
 *   │   + Plus   │                                  │
 *   │ ├──────────┤                                  │
 *   │ • 星标客户 │ avatar grid of tagged contacts   │
 *   │ • 内部同事 │                                  │
 *   └────────────┴──────────────────────────────────┘
 *
 * Create/rename are handled by a modal dialog (the reference flow); the
 * backend calls (useLabels → /Label/Add|UpdateName|Delete|GetList) are
 * unchanged.
 */
export function LabelsView({
    account,
    contacts,
}: {
    account: Account | null
    contacts: Contact[]
}) {
    const {labels, loading, refreshing, refresh, create, rename, remove} = useLabels(account)

    const [selectedId, setSelectedId] = useState<number | null>(null)
    const [modalOpen, setModalOpen] = useState(false)
    const [modalEditing, setModalEditing] = useState<ContactLabel | null>(null)
    const [modalName, setModalName] = useState('')
    const [busy, setBusy] = useState(false)
    const [profileContact, setProfileContact] = useState<Contact | null>(null)
    const [profileAnchor, setProfileAnchor] = useState<DOMRect | null>(null)

    const contactsByLabel = useMemo(() => {
        const byLabelId = new Map<number, Contact[]>()
        for (const contact of contacts) {
            if (!contact.labelIds) continue
            for (const raw of contact.labelIds.split(',')) {
                const id = Number(raw.trim())
                if (!Number.isFinite(id) || id <= 0) continue
                if (!byLabelId.has(id)) byLabelId.set(id, [])
                byLabelId.get(id)!.push(contact)
            }
        }
        return byLabelId
    }, [contacts])

    // Keep the selection aligned with the latest labels list. Fall back to
    // the first label when the currently selected one has been deleted, or
    // when no selection has been made yet.
    useEffect(() => {
        if (!labels.length) {
            if (selectedId !== null) setSelectedId(null)
            return
        }
        if (selectedId === null || !labels.some(l => l.id === selectedId)) {
            setSelectedId(labels[0].id)
        }
    }, [labels, selectedId])

    const selectedTag = useMemo(
        () => labels.find(l => l.id === selectedId) ?? null,
        [labels, selectedId],
    )
    const selectedMembers = selectedTag ? contactsByLabel.get(selectedTag.id) ?? [] : []

    const disabled = !account || busy || refreshing

    function openCreateModal() {
        setModalEditing(null)
        setModalName('')
        setModalOpen(true)
    }

    function openRenameModal(label: ContactLabel) {
        setModalEditing(label)
        setModalName(label.name)
        setModalOpen(true)
    }

    function closeModal() {
        setModalOpen(false)
        setModalEditing(null)
        setModalName('')
    }

    async function handleSaveModal() {
        const name = modalName.trim()
        if (!name) {
            message.warning('标签名称不能为空')
            return
        }
        if (labels.some(l => l.name === name && l.id !== modalEditing?.id)) {
            message.error('标签名称已存在')
            return
        }
        setBusy(true)
        try {
            if (modalEditing) {
                await rename(modalEditing.id, name)
                message.success('标签修改成功')
            } else {
                await create(name)
                message.success('新标签添加成功')
            }
            closeModal()
        } finally {
            setBusy(false)
        }
    }

    async function handleDelete(label: ContactLabel) {
        const count = contactsByLabel.get(label.id)?.length ?? 0
        const suffix = count > 0 ? `，该标签关联 ${count} 位联系人` : ''
        if (!window.confirm(`确定删除标签「${label.name}」吗${suffix}？`)) return
        setBusy(true)
        try {
            await remove(label.id)
            message.success('标签已删除')
        } finally {
            setBusy(false)
        }
    }

    return (
        <section className="flex flex-col h-full bg-page">
            <PageHeader
                title="标签管理"
                action={
                    <button
                        type="button"
                        className="btn-secondary"
                        disabled={disabled}
                        onClick={() => void refresh()}
                        title="从微信服务器重新同步标签"
                    >
                        <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''}/>
                        刷新
                    </button>
                }
            />
            <div className="flex-1 flex flex-col md:flex-row overflow-y-auto md:overflow-hidden p-3 md:p-6 gap-3 md:gap-6 relative">
                {/* Left — tag list card */}
                <div className="w-full md:w-[300px] bg-white rounded-lg shadow-card border border-hair flex flex-col shrink-0 h-auto md:h-full overflow-hidden">
                    <div className="p-3 border-b border-hair bg-page flex justify-between items-center">
                        <span className="font-medium text-ink ml-1">所有标签</span>
                        <button
                            type="button"
                            className="p-1.5 bg-white border border-hair rounded hover:bg-page transition-colors text-soft disabled:opacity-60 disabled:cursor-not-allowed"
                            title="新建标签"
                            disabled={disabled}
                            onClick={openCreateModal}
                        >
                            <Plus size={16}/>
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {labels.length === 0 ? (
                            <div className="text-center text-muted text-sm mt-10">
                                {loading || refreshing ? '正在加载标签…' : '暂无任何标签'}
                            </div>
                        ) : (
                            labels.map((label) => {
                                const isActive = selectedId === label.id
                                const count = contactsByLabel.get(label.id)?.length ?? 0
                                return (
                                    <div
                                        key={label.id}
                                        onClick={() => setSelectedId(label.id)}
                                        className={`group flex items-center justify-between p-3 cursor-pointer border-b border-hair/60 transition-colors ${
                                            isActive
                                                ? 'bg-subActive text-white'
                                                : 'hover:bg-page'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3 overflow-hidden">
                                            <Tag
                                                size={16}
                                                className={isActive ? 'text-white' : 'text-accent'}
                                            />
                                            <span
                                                className={`text-sm truncate ${
                                                    isActive ? 'font-medium text-white' : 'text-ink'
                                                }`}
                                            >
                                                {label.name}{' '}
                                                <span className="opacity-80">({count})</span>
                                            </span>
                                        </div>
                                        <div
                                            className={`flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity ${
                                                isActive ? 'text-white' : 'text-muted'
                                            }`}
                                        >
                                            <Edit
                                                size={14}
                                                className="cursor-pointer hover:scale-110 transition-transform"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    openRenameModal(label)
                                                }}
                                            />
                                            <Trash2
                                                size={14}
                                                className="cursor-pointer hover:scale-110 hover:text-red-500 transition-transform"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    void handleDelete(label)
                                                }}
                                            />
                                        </div>
                                    </div>
                                )
                            })
                        )}
                    </div>
                </div>

                {/* Right — members of the selected tag */}
                <div className="flex-1 min-h-[320px] md:min-h-0 bg-white rounded-lg shadow-card border border-hair flex flex-col h-auto md:h-full overflow-hidden p-4 md:p-6">
                    {selectedTag ? (
                        <>
                            <div className="flex justify-between items-center mb-6 shrink-0 border-b border-hair pb-4">
                                <div className="flex items-center gap-2">
                                    <h2 className="text-xl font-medium text-ink">{selectedTag.name}</h2>
                                    <span className="text-sm bg-page text-soft px-2 py-0.5 rounded-full">
                                        {selectedMembers.length} 人
                                    </span>
                                </div>
                                <span className="text-xs text-muted">
                                    通过联系人详情页为其添加标签
                                </span>
                            </div>
                            <div className="flex-1 overflow-y-auto min-h-0 pt-2">
                                {selectedMembers.length > 0 ? (
                                    <div className="grid grid-cols-2 gap-6 sm:grid-cols-4 lg:grid-cols-6 2xl:grid-cols-8">
                                        {selectedMembers.map((member) => (
                                            <div
                                                key={member.wxid}
                                                className="flex flex-col items-center group cursor-pointer"
                                                onClick={(e) => {
                                                    const rect = (e.currentTarget.querySelector('img, div') as HTMLElement)?.getBoundingClientRect()
                                                    setProfileContact(member)
                                                    setProfileAnchor(rect || null)
                                                }}
                                            >
                                                <img
                                                    src={avatarDisplaySrc(member.avatar, member.wxid)}
                                                    alt=""
                                                    className="h-14 w-14 shrink-0 rounded bg-page mb-2 group-hover:shadow-card group-hover:ring-2 group-hover:ring-accent/30 transition-all"
                                                />
                                                <span className="text-xs text-soft truncate w-full text-center">
                                                    {member.remark || member.nickName || member.name || member.wxid}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center h-full text-muted opacity-60">
                                        <User size={48} className="mb-4"/>
                                        <p>该标签下暂无好友</p>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center justify-center h-full text-muted">
                            请在左侧选择或新建一个标签
                        </div>
                    )}
                </div>

                {/* Create/Rename modal */}
                {modalOpen ? (
                    <div className="absolute inset-0 z-overlay bg-black/40 flex items-center justify-center backdrop-blur-sm">
                        <div className="bg-white rounded-lg shadow-card border border-hair w-full max-w-[360px] overflow-hidden flex flex-col">
                            <div className="px-5 py-4 border-b border-hair flex justify-between items-center bg-page">
                                <h3 className="text-[15px] font-medium text-ink">
                                    {modalEditing ? '编辑标签' : '新建标签'}
                                </h3>
                                <X
                                    size={18}
                                    className="text-muted cursor-pointer hover:text-soft transition-colors"
                                    onClick={closeModal}
                                />
                            </div>
                            <div className="p-5">
                                <input
                                    type="text"
                                    value={modalName}
                                    onChange={(e) => setModalName(e.target.value)}
                                    autoFocus
                                    maxLength={16}
                                    className="w-full border border-hair rounded-md p-2.5 text-sm focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
                                    placeholder="请输入标签名称"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault()
                                            void handleSaveModal()
                                        } else if (e.key === 'Escape') {
                                            e.preventDefault()
                                            closeModal()
                                        }
                                    }}
                                />
                            </div>
                            <div className="px-5 py-3 border-t border-hair bg-page flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="px-4 py-1.5 bg-white border border-hair rounded text-soft text-sm hover:bg-page transition-colors"
                                >
                                    取消
                                </button>
                                <button
                                    type="button"
                                    onClick={() => void handleSaveModal()}
                                    disabled={busy || !modalName.trim()}
                                    className="px-4 py-1.5 bg-accent text-white rounded text-sm hover:bg-accent-hover transition-colors shadow-card disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    保存
                                </button>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>

            {/* Contact profile popover */}
            {profileContact && (
                <ContactProfilePopover
                    anchorRect={profileAnchor}
                    avatar={avatarDisplaySrc(profileContact.avatar, profileContact.wxid)}
                    latestAvatarUrl={contactLatestAvatarUrl(profileContact)}
                    displayName={displayName(profileContact)}
                    wxid={profileContact.wxid}
                    remark={profileContact.remark}
                    nickName={profileContact.nickName}
                    alias={profileContact.alias}
                    region={[profileContact.province, profileContact.city].filter(Boolean).join(' ') || undefined}
                    signature={profileContact.signature}
                    sex={profileContact.sex}
                    onClose={() => { setProfileContact(null); setProfileAnchor(null) }}
                />
            )}
        </section>
    )
}
